import React, { useState, useEffect } from "react";
import {
  Droplets,
  Truck,
  CreditCard,
  MapPinHouse,
  UserRound,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import {
  useParams,
  Link,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import UserJugs from "./pages-dashboard/UserJugs";
import UserOrders from "./pages-dashboard/UserOrders";
import UserPayments from "./pages-dashboard/UserPayments";
import UserLocations from "./pages-dashboard/UserLocations";
import UserProfile from "./pages-dashboard/UserProfile";
import { Button } from "@radix-ui/themes";

const Dashboard = () => {
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();

  // Inicialización y manejo de responsive
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
      
      // En desktop, mantener sidebar abierto por defecto
      // En móvil, cerrarlo por defecto
      if (newIsMobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
      
      setIsInitialized(true);
    };

    handleResize(); // Ejecutar inmediatamente
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  function getPathName() {
    switch (location.pathname) {
      case "/app":
        return "Tus botellones";
      case "/app/pedidos":
        return "Pedidos";
      case "/app/pagos":
        return "Metodos de pago";
      // case "/app/direcciones":
      //   return "Direcciones";
      case "/app/perfil":
        return "Tu perfil";
      default:
        return "Dashboard";
    }
  }

  const SidebarLink = ({ to, icon: Icon, children }) => {
    const isActive = location.pathname === to;

    return (
      <Link
        to={to}
        className={`
          flex items-center space-x-3 px-4 py-3 mx-2 rounded-lg
          transition-all duration-200 relative group
          ${isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          }
        `}
        onClick={() => isMobile && setSidebarOpen(false)}
      >
        <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? "text-blue-700" : ""}`} />
        
        {/* Texto con animación */}
        <span className={`
          transition-all duration-300 whitespace-nowrap font-semibold text-base
          ${sidebarOpen || isMobile ? "opacity-100" : "opacity-0 w-0 overflow-hidden"}
        `}>
          {children}
        </span>
        
        {/* Tooltip para cuando está colapsado en desktop */}
        {!sidebarOpen && !isMobile && (
          <div className="
            absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm 
            rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200
            pointer-events-none whitespace-nowrap z-50
          ">
            {children}
          </div>
        )}
      </Link>
    );
  };

  // Pantalla de carga para prevenir flash
  if (!isInitialized) {
    return <div className="min-h-screen bg-gray-100" />;
  }

  return (
    <div className="relative flex h-screen bg-gray-100 overflow-hidden">
      {/* Overlay mejorado para móvil */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50
          bg-white border-r border-gray-200 
          flex flex-col shadow-lg
          transition-all duration-300 ease-in-out
          ${isMobile 
            ? sidebarOpen 
              ? "translate-x-0 w-64" 
              : "-translate-x-full w-64"
            : sidebarOpen 
              ? "w-64" 
              : "w-20"
          }
        `}
      >
        {/* Header del sidebar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200  max-h-[96px] min-h-[96px]">
          {sidebarOpen && (
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-900 no-underline transition-colors"
            >
              <img
                src="/assets/logos/fluenti-logo-black-140.svg"
                alt="logo"
                className="h-16 w-auto"
              />
            </Link>
          )}
          
          {/* Botón para cerrar en móvil o toggle en desktop */}
          <Button
            variant="ghost"
            size="1"
            onClick={toggleSidebar}
            className={`
              flex-shrink-0 h-8 w-8 p-1 flex items-center justify-center
              ${sidebarOpen && !isMobile ? "ml-auto" : ""}
              ${!sidebarOpen ? "mx-auto" : ""}
            `}
            aria-label={sidebarOpen ? "Contraer menú" : "Expandir menú"}
          >
            {isMobile ? (
              sidebarOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )
            ) : (
              // En desktop usar chevrones
              sidebarOpen ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )
            )}
          </Button>
        </div>

        {/* Navegación */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <SidebarLink to={`/app`} icon={Droplets}>
            Tus botellones
          </SidebarLink>
          <SidebarLink to={`/app/pedidos`} icon={Truck}>
            Pedidos
          </SidebarLink>
          {/* <SidebarLink to={`/app/pagos`} icon={CreditCard}>
            Métodos de pago
          </SidebarLink> */}
          {/* <SidebarLink to={`/app/direcciones`} icon={MapPinHouse}>
            Direcciones
          </SidebarLink> */}
          <SidebarLink to={`/app/perfil`} icon={UserRound}>
            Perfil
          </SidebarLink>
        </nav>
      </aside>

      {/* Contenido principal */}
      <div className={`
        flex-1 flex flex-col overflow-hidden
        transition-all duration-300 ease-in-out
        ${!isMobile && !sidebarOpen ? "ml-20" : "ml-0"}
      `}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-6xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <div className="flex items-center gap-4">
              {/* Botón de menú para móvil cuando sidebar está cerrado */}
              {isMobile && !sidebarOpen && (
                <Button
                  variant="ghost"
                  size="1"
                  onClick={toggleSidebar}
                  className="h-8 w-8 p-1 flex items-center justify-center md:hidden"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              )}
              
              <h1 className="text-3xl font-bold text-gray-900 fraunces py-3 max-h-[96px]">
                {getPathName()}
              </h1>
            </div>

            {/* Botón adicional de acciones si es necesario */}
            <div className="flex items-center gap-2">
              {/* Aquí puedes agregar botones adicionales como notificaciones, etc */}
            </div>
          </div>
        </header>

        {/* Contenido principal */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-6xl mx-auto py-6 px-4 lg:px-8">
            <Routes>
              <Route index element={<UserJugs />} />
              <Route path="/pedidos" element={<UserOrders />} />
              <Route path="/pagos" element={<UserPayments />} />
              <Route path="/direcciones" element={<UserLocations />} />
              <Route path="/perfil" element={<UserProfile />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;